import { useState } from "react";
import { useChatHistory } from "../../../hooks/useChatHistory";
import CbTable from "../../../components/CbTable";
import { CustomToolbar } from "../../../components/table-toolbar";
import { Spin } from "antd";
import dayjs from "dayjs";
import Markdown from "react-markdown";
import { markdownStyles } from "../../../components/Markdown/markdownStyle";
import { Toolbar, ToolbarHeading } from "@/layouts/demo1/toolbar";
import { Container } from "@/components/container";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import { TablePagination } from "../../../components/pagination/TablePagination";
import { faFacebook } from "@fortawesome/free-brands-svg-icons";
import zaloIcon from "@/assets/images/zalo-icon.png";
import TargetCount from "./components/TargetCount";
import { RiRobot2Line } from "react-icons/ri";
import { FaRegUser } from "react-icons/fa6";
import { RiVoiceprintFill } from "react-icons/ri";
import { RiGlobalLine } from "react-icons/ri";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import ChainIcon from "@/assets/icons/ChainIcon";
import ChainOffIcon from "@/assets/icons/ChainOffIcon";

const AgentsHistoryPage = () => {
  const intl = useIntl();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [expandedRows, setExpandedRows] = useState({});
  const [statusValue, setStatusValue] = useState();
  const [searchDraft, setSearchDraft] = useState({
    message: "",
    target: "",
    page: 1,
    limit: 50,
    start_date: "",
    end_date: "",
  });

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 50,
  });

  const { chatHistoryData, isHistoryLoading } = useChatHistory(searchParams);

  const getMessageSource = (target) => {
    switch (Number(target)) {
      case 1:
        return (
          <div className="flex items-center justify-center">
            <img src={zaloIcon} alt="Zalo" className="size-6" />
          </div>
        );
      case 0:
        return (
          <FontAwesomeIcon
            icon={faFacebook}
            className="size-6 text-blue-500"
            title="Facebook"
          />
        );
      case 2:
        return (
          <div className="flex items-center justify-center" title="Internet">
            <RiGlobalLine className="size-6 text-blue-500" />
          </div>
        );
      default:
        return "Unknown";
    }
  };

  const getMessageType = (use) => {
    switch (use) {
      case 0:
        return (
          <div
            className="flex items-center justify-center"
            title={intl.formatMessage({ id: "HISTORY.USER_MESSAGE_TYPE" })}
          >
            <FaRegUser className="size-6" />
          </div>
        );
      case 1:
        return (
          <div
            className="flex items-center justify-center"
            title={intl.formatMessage({ id: "HISTORY.BOT_MESSAGE_TYPE" })}
          >
            <RiRobot2Line className="size-6" />
          </div>
        );
      case 3:
        return (
          <div
            className="flex items-center justify-center gap-2"
            title={intl.formatMessage({ id: "HISTORY.USER_VOICE_MESSAGE" })}
          >
            <FaRegUser className="size-5" />
            <RiVoiceprintFill className="size-5" />
          </div>
        );
      case 4:
        return (
          <div
            className="flex items-center justify-center gap-2"
            title={intl.formatMessage({ id: "HISTORY.BOT_VOICE_MESSAGE" })}
          >
            <RiRobot2Line className="size-6" />
            <RiVoiceprintFill className="size-5" />
          </div>
        );
      default:
        return "Unknown";
    }
  };

  const sourceOptions = [
    {
      key: "all",
      label: (
        <div className="flex items-center gap-5">
          <FontAwesomeIcon
            icon={faListCheck}
            className="size-6 text-[#52c41a]"
            title="Facebook"
          />
          <FormattedMessage id="HISTORY.ALL" />
        </div>
      ),
      value: "",
    },
    {
      key: "facebook",
      label: (
        <div className="flex items-center gap-5">
          <FontAwesomeIcon
            icon={faFacebook}
            className="size-6 text-blue-500"
            title="Facebook"
          />
          <label>Facebook</label>
        </div>
      ),
      value: "0",
    },
    {
      key: "zalo",
      label: (
        <div className="flex items-center gap-5">
          <img src={zaloIcon} alt="Zalo" className="size-6" />
          <label>Zalo</label>
        </div>
      ),
      value: "1",
    },
    {
      key: "web",
      label: (
        <div className="flex items-center gap-5">
          <RiGlobalLine className="size-6 text-blue-500" />
          <label>Web</label>
        </div>
      ),
      value: "2",
    },
  ];

  const statusOptions = [
    {
      key: "all",
      label: (
        <div className="flex items-center gap-5">
          <FontAwesomeIcon
            icon={faListCheck}
            className="size-6 text-[#52c41a]"
            title="Facebook"
          />
          <FormattedMessage id="HISTORY.ALL" />
        </div>
      ),
      value: "",
    },
    {
      key: "online",
      label: (
        <div className="flex items-center gap-5">
          <ChainIcon className="size-6 text-[#52c41a]" />
          <FormattedMessage id="HISTORY.ONLINE" />
        </div>
      ),
      value: true,
    },
    {
      key: "offline",
      label: (
        <div className="flex items-center gap-5">
          <ChainOffIcon className="size-6 text-red-500" />
          <FormattedMessage id="HISTORY.OFFLINE" />
        </div>
      ),
      value: false,
    },
  ];

  const fiteredChatHistory = chatHistoryData?.result.filter((item) => {
    if (statusValue !== undefined && statusValue !== "") {
      return item.is_online === statusValue;
    }
    return chatHistoryData?.result;
  });

  // Function to check if message needs expansion (is long/multi-line)
  const isMessageLong = (text) => {
    if (!text) return false;

    // Check if text has multiple lines or is longer than 100 characters
    const hasMultipleLines = text.includes("\n");
    const isLongText = text.length > 60;

    return hasMultipleLines || isLongText;
  };

  const getConversationStatus = (is_online) => {
    if (is_online) {
      return (
        <div className="flex justify-center items-center gap-2" title="Online">
          <ChainIcon className="size-6 text-[#52c41a]" />
        </div>
      );
    } else {
      return (
        <div className="flex justify-center items-center gap-2" title="Offline">
          <ChainOffIcon className="size-6 text-red-500" />
        </div>
      );
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      align: "center",
      width: "6rem",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: <FormattedMessage id="HISTORY.MESSAGE" />,
      titleAlign: "center",
      dataIndex: "message",
      key: "message",
      align: "left",
      width: "20rem",
      render: (text, record) => {
        const isExpanded = expandedRows[record.id] || false;
        const needsExpansion = isMessageLong(text);

        return (
          <div className="text-sm md:text-base w-full flex flex-col transition-all duration-300 ease-in-out">
            {record.voice !== "" && (
              <audio controls className="h-[40px] w-full mb-2">
                <source
                  src={`https://audio.chatme.vn/${record.voice}`}
                  type="audio/ogg"
                />
              </audio>
            )}
            <div className="flex items-start">
              <div
                className={`${
                  !isExpanded && needsExpansion ? "line-clamp-1" : ""
                } flex-1 transition-all duration-300 ease-in-out overflow-hidden`}
              >
                <Markdown
                  components={markdownStyles}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {text}
                </Markdown>
              </div>
              {needsExpansion && (
                <button
                  onClick={() =>
                    setExpandedRows((prev) => ({
                      ...prev,
                      [record.id]: !isExpanded,
                    }))
                  }
                  title={`${isExpanded ? "Thu nhỏ" : "Phóng to"}`}
                  className="ml-2 flex-shrink-0"
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className={`h-3 w-3 leading-none text-gray-500`}
                    // className={`h-3 w-3 text-gray-500 ${isExpanded ? '!text-[#50B553]' : ''}`}
                  />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: <FormattedMessage id="HISTORY.SOURCE" />,
      dataIndex: "target",
      key: "target",
      align: "center",
      width: "7rem",
      render: (target) => getMessageSource(target),
    },
    {
      title: <FormattedMessage id="HISTORY.TYPE" />,
      dataIndex: "use",
      key: "use",
      align: "center",
      width: "8rem",
      render: (use) => getMessageType(use),
    },
    {
      title: <FormattedMessage id="HISTORY.TIME" />,
      dataIndex: "created_at",
      align: "center",
      key: "created_at",
      width: "12rem",
      render: (text) => <p>{dayjs(text).format("HH:mm:ss DD-MM-YYYY")}</p>,
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      title: <FormattedMessage id="HISTORY.SID" />,
      dataIndex: "sid",
      align: "center",
      key: "sid",
      width: "13rem",
    },
    {
      title: <FormattedMessage id="HISTORY.STATUS" />,
      dataIndex: "is_online",
      align: "center",
      key: "is_online",
      width: "6rem",
      render: (is_online) => getConversationStatus(is_online),
    },
  ];

  const handleSearchChange = (e) => {
    setSearchDraft({ ...searchDraft, message: e.target.value });
  };
  const handleSourceChange = (value) => {
    setSearchDraft({ ...searchDraft, target: value?.toString() });
    setSearchParams({ ...searchDraft, target: value?.toString() });
  };
  const handleSearch = () => {
    setSearchParams({ ...searchParams, ...searchDraft });
  };
  const handleStatusChange = (value) => {
    setStatusValue(value);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      // Chuyển đổi dayjs objects thành timestamps milisecond
      const startTimestamp = dates[0].valueOf();
      const endTimestamp = dates[1].valueOf();

      setSearchDraft({
        ...searchDraft,
        start_date: startTimestamp,
        end_date: endTimestamp,
      });
    } else {
      // Reset về trạng thái ban đầu khi không có dates hoặc dates không hợp lệ
      setSearchDraft({
        ...searchDraft,
        start_date: "",
        end_date: "",
      });
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: newPage,
      limit: limit,
    }));
  };
  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: 1,
      limit: newLimit,
    }));
  };

  return (
    <>
      <Toolbar>
        <Container>
          <ToolbarHeading title={<FormattedMessage id={"USER.MENU.HISTORY"} />} />
        </Container>
      </Toolbar>
      <div className="p-3 md:p-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          {sourceOptions.map((item) => (
            <TargetCount key={item.key} target={item} />
          ))}
        </div>
        <CustomToolbar
          sourceOptions={sourceOptions}
          searchValue={searchDraft.message}
          onSearchChange={handleSearchChange}
          // sourceValue={searchDraft.target?.toString()}
          onSourceChange={handleSourceChange}
          dateRange={
            searchDraft.start_date && searchDraft.end_date
              ? [dayjs(searchDraft.start_date), dayjs(searchDraft.end_date)]
              : []
          }
          onDateRangeChange={handleDateRangeChange}
          onSearch={handleSearch}
          statusOptions={statusOptions}
          statusValue={statusValue}
          onStatusChange={handleStatusChange}
        />
        <div className="mb-4 flex justify-end">
          <TablePagination
            current={page}
            pageSize={limit}
            total={chatHistoryData?.total || 0}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["10", "20", "50", "80", "100"]}
          />
        </div>
        {isHistoryLoading ? (
          <div className="min-h-[500px] flex justify-center items-center">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <CbTable
              columns={columns}
              dataSource={fiteredChatHistory}
              pagination={false}
              scroll={{ x: 500 }}
            />
          </>
        )}
        <div className="mt-4 flex justify-end">
          <TablePagination
            current={page}
            pageSize={limit}
            total={chatHistoryData?.total || 0}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["10", "20", "50", "80", "100"]}
          />
        </div>
      </div>
    </>
  );
};

export { AgentsHistoryPage };
